import { useEffect, useState } from 'react';
import { api } from '../services/api';
import type { AnalyticsOverview } from '../types';
import { Layout } from '../components/layout/Layout';
import { StatCard } from '../components/analytics/StatCard';
import { Users, Bot, Heart } from 'lucide-react';

function SkeletonCard() {
  return (
    <div className="bg-[#141420] border border-[#252535] rounded-2xl p-5">
      <div className="flex items-start justify-between">
        <div className="flex-1 space-y-3">
          <div className="h-3 w-24 animate-shimmer rounded-md" />
          <div className="h-8 w-20 animate-shimmer rounded-lg" />
          <div className="h-3 w-16 animate-shimmer rounded-md" />
        </div>
        <div className="w-11 h-11 animate-shimmer rounded-xl ml-3" />
      </div>
    </div>
  );
}

export function Dashboard() {
  const [overview, setOverview] = useState<AnalyticsOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const overviewData = await api.getAnalyticsOverview();
        setOverview(overviewData);
      } catch (err: any) {
        setError(err.message || 'Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (error) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="w-12 h-12 bg-red-500/10 rounded-2xl flex items-center justify-center mx-auto mb-3">
              <span className="text-[#ef4444] text-xl">!</span>
            </div>
            <p className="text-[#ef4444] font-medium">Failed to load dashboard</p>
            <p className="text-[#64748b] text-sm mt-1">{error}</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 animate-fade-in">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-[#f1f5f9]">Dashboard</h2>
            <p className="text-[#64748b] mt-1 text-sm">
              Monitor your platform metrics and recent activity
            </p>
          </div>
          <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-xl bg-[#141420] border border-[#252535] text-xs text-[#64748b]">
            <span className="w-1.5 h-1.5 bg-[#22c55e] rounded-full animate-pulse" />
            Live data
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {loading ? (
            <>
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </>
          ) : overview ? (
            <>
              <StatCard
                title="Total Users"
                value={overview.totalUsers}
                icon={<Users className="w-5 h-5" />}
                trend={12}
                trendLabel="vs last month"
                accent="blue"
                to="/users"
              />
              <StatCard
                title="Total Twins"
                value={overview.totalTwins}
                icon={<Bot className="w-5 h-5" />}
                trend={8}
                trendLabel="vs last month"
                accent="purple"
                to="/twins"
              />
              <StatCard
                title="Total Matches"
                value={overview.totalMatches}
                icon={<Heart className="w-5 h-5" />}
                trend={24}
                trendLabel="vs last month"
                accent="red"
                to="/analytics#recent-matches"
              />
            </>
          ) : null}
        </div>
      </div>
    </Layout>
  );
}
